import { IconName } from "@zenchef/icomoon/dist/aromaticons/types";
import {
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";
import { TestingAttributes } from "../../utils/createTestId";
import { Collapsible, useSegmentGroupContext } from "@ark-ui/react";
import { HStack, Stack } from "@zenchef/styled-system/jsx";
import Icon from "../Icon/Icon";
import { Text } from "../typography/Text";
import SidebarContext from "./SidebarContext";
import { OpenChangeDetails } from "@ark-ui/react/dist/components/collapsible/collapsible";
import { STANDALONE_SECTION_PREFIX } from "./Sidebar";
import SideBarItem, { SideBarItemProps } from "./SidebarItem";
import withTestId from "../../utils/withTestId";

interface BaseSideBarSectionProps {
  title: string;
  icon: IconName;
  disabled?: boolean;
  tagComponent?: ReactNode;
}

export interface SideBarSectionWithChildrenProps
  extends BaseSideBarSectionProps,
    PropsWithChildren {
  value?: never;
  as?: never;
  "data-testid"?: never;
}

export type SideBarSectionWithoutChildrenProps<T extends React.ElementType> =
  BaseSideBarSectionProps &
    SideBarItemProps<T> & {
      children?: never;
      "data-testid"?: TestingAttributes["testId"];
    };

export type SideBarSectionProps<T extends React.ElementType> =
  | SideBarSectionWithChildrenProps
  | SideBarSectionWithoutChildrenProps<T>;

function SideBarSection<T extends React.ElementType>({
  title,
  icon,
  children,
  disabled,
  value,
  tagComponent,
  ...props
}: SideBarSectionProps<T>) {
  const contentRef = useRef<HTMLDivElement | null>(null);

  const { open, selectedSection, setSelectedSection } =
    useContext(SidebarContext);

  const computeHasSelectedItem = useCallback(
    () =>
      Boolean(
        contentRef.current?.querySelector(
          '[data-scope="segment-group"][data-part="item"][data-state="checked"]',
        ),
      ),
    [contentRef.current],
  );
  const hasSelectedItem = computeHasSelectedItem();

  const context = useSegmentGroupContext();
  const { value: currentValue } = context;
  const isStandalone = children === undefined && value !== undefined;

  useEffect(() => {
    hasSelectedItem && setSelectedSection?.(title);
    context.focus();
  }, [currentValue, hasSelectedItem]);

  const handleOpenChange = ({ open }: OpenChangeDetails) => {
    if (!isStandalone && setSelectedSection) {
      setSelectedSection(open ? title : "");
    }
  };

  if (value !== undefined) {
    const { as } = props;
    return (
      <SideBarItem
        {...props}
        title={
          <HStack
            justifyContent="space-between"
            width={open ? "100%" : "fit-content"}
          >
            <HStack width="100%">
              <Icon name={icon} />
              <Text textStyle="title.m" data-part="section-title">
                {title}
              </Text>
            </HStack>
            {open && tagComponent}
          </HStack>
        }
        value={`${STANDALONE_SECTION_PREFIX}${value}`}
        onClick={() => setSelectedSection?.("")}
        disabled={disabled}
        as={as ?? "button"}
      />
    );
  }

  return (
    <Collapsible.Root
      disabled={disabled}
      open={selectedSection === title || computeHasSelectedItem()}
      onOpenChange={handleOpenChange}
    >
      <Stack gap="gap.2">
        <Collapsible.Trigger
          disabled={disabled}
          aria-checked={hasSelectedItem}
          data-testid={props["data-testid"]}
        >
          <HStack
            justifyContent="space-between"
            width={open ? "100%" : "fit-content"}
          >
            <HStack width="100%">
              <Icon name={icon} />
              <Text textStyle="title.m" data-part="section-title">
                {title}
              </Text>
            </HStack>
            {open && tagComponent}
          </HStack>
        </Collapsible.Trigger>
        <Collapsible.Content asChild ref={contentRef}>
          <Stack gap="gap.0">{children}</Stack>
        </Collapsible.Content>
      </Stack>
    </Collapsible.Root>
  );
}

export default withTestId("nav")(SideBarSection);
